export const ADMIN_STAFF_ROLES = ['manager', 'accountant', 'support'] as const;

export type AdminStaffRole = (typeof ADMIN_STAFF_ROLES)[number];

export const ADMIN_PORTAL_ROLES = [
  'superAdmin',
  'admin',
  ...ADMIN_STAFF_ROLES,
] as const;

export type AdminPortalRole = (typeof ADMIN_PORTAL_ROLES)[number];

export const EMPLOYEE_ROLE_OPTIONS: {
  label: string;
  value: AdminStaffRole;
}[] = [
  { label: 'Manager', value: 'manager' },
  { label: 'Accountant', value: 'accountant' },
  { label: 'Customer Support', value: 'support' },
];

const FULL_ADMIN_ACCESS = ['superAdmin', 'admin'] as const;

/** Route prefix -> roles allowed (superAdmin/admin always allowed via guard). */
export const ADMIN_ROUTE_ACCESS: Record<string, readonly string[]> = {
  '/dashboard': ['manager', 'accountant', 'support'],
  '/withdrawal-requests': ['manager', 'accountant'],
  '/dynamic-services': ['manager'],
  '/dynamic-old-services': ['manager'],
  '/approvals': ['manager', 'support'],
  '/users': ['manager', 'support'],
  '/deletion-requests': ['manager', 'support', 'assistant'],
  '/qa-test-panel': ['manager', 'support', 'assistant', 'accountant'],
  '/admin/orders': ['manager', 'accountant', 'support'],
  '/employees': [],
  '/cars': ['manager'],
  '/shop-management': ['manager'],
  '/analytics': ['manager', 'accountant'],
  '/admin/payments': ['manager', 'accountant'],
  '/admin/payment-requests': ['manager', 'accountant'],
  '/offers-ads': ['manager'],
  '/admin-offers': ['manager'],
  '/app-control': ['manager'],
  '/ads': ['manager'],
  '/app-layouts': ['manager'],
  '/legalss': ['manager', 'support'],
  '/greetings': ['manager'],
  '/nojoom': ['manager'],
  '/bakat': ['manager'],
  '/legals': ['manager', 'support'],
  '/admin/profile': ['manager', 'accountant', 'support'],
  '/feedbacks': ['manager', 'support'],
  '/admin/help-center': ['manager', 'support'],
  '/admin/docs': ['manager', 'accountant', 'support'],
};

export function isAdminPortalRole(role: string | undefined | null): boolean {
  if (!role) return false;
  return (ADMIN_PORTAL_ROLES as readonly string[]).includes(role);
}

export function isAdminStaffRole(role: string | undefined | null): boolean {
  if (!role) return false;
  return (ADMIN_STAFF_ROLES as readonly string[]).includes(role);
}

export function getEmployeeRoleLabel(role: string | undefined | null): string {
  const found = EMPLOYEE_ROLE_OPTIONS.find((item) => item.value === role);
  return found?.label || role || '—';
}

export function canAccessAdminPath(
  role: string | undefined | null,
  url: string,
): boolean {
  if (!role) return false;
  if ((FULL_ADMIN_ACCESS as readonly string[]).includes(role)) {
    return true;
  }

  const path = url.split('?')[0];
  const prefixes = Object.keys(ADMIN_ROUTE_ACCESS).sort(
    (a, b) => b.length - a.length,
  );

  for (const prefix of prefixes) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      const allowedStaff = ADMIN_ROUTE_ACCESS[prefix] || [];
      return (allowedStaff as readonly string[]).includes(role);
    }
  }

  return isAdminStaffRole(role);
}

export function getPostLoginRoute(
  role: string | undefined | null,
  isApproved?: boolean,
): string {
  if (role === 'company') {
    // Only fully approved companies enter the dashboard. Missing/undefined
    // isApproved (e.g. right after signup before Firestore settles) must not
    // fall through to /profile or /my-dashboard.
    return isApproved === true ? '/my-dashboard' : '/account-pending';
  }
  if (isAdminPortalRole(role)) {
    return '/dashboard';
  }
  if (role === 'customer' || role === 'solosp' || role === 'cprovider') {
    return '/aunak-pro-app';
  }
  return '/profile';
}
